/**
 * Panneau « Feuilles de brouillon » de l'éditeur V2 : bilan des feuilles de
 * l'exercice, génération de celles qui manquent (une question après l'autre,
 * avec l'avancement), publication des feuilles prêtes, accès au test dans le
 * player.
 *
 * Pour l'instant, seules les questions reliées à une SKILLCARD ont une feuille.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Loader2, PenLine, Play, Sparkles, Square } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import {
  generationErrorMessage,
  isEngineReady,
  isFatalGenerationError,
  SHEET_STATE_LABEL,
  type SheetState,
} from './scratch-admin';
import { SheetStatePill } from './question-scratch-tools';
import {
  useGenerateSheet,
  useIsGenerating,
  useSetSheetStatus,
  useSheetQuestions,
  type SheetQuestion,
} from './use-scratch-admin';

const BTN =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border px-4 py-2 font-sans text-sm font-semibold transition-colors active:scale-[0.98] disabled:opacity-50';

const ORDER: SheetState[] = ['published', 'ready', 'review', 'disabled', 'ineligible', 'none'];

/**
 * Questions reliées à générer en lot : sans feuille, ou dont la feuille est à
 * revoir (refusée par le moteur, ou périmée car le QCM a changé). Une feuille
 * publiée périmée n'est pas régénérée en lot : la question est traitée une à
 * une (confirmation).
 */
export function questionsToGenerate(questions: SheetQuestion[]): SheetQuestion[] {
  return questions.filter(
    (q) =>
      q.linked &&
      !q.blocker &&
      q.state !== 'published' &&
      q.state !== 'ineligible' &&
      // une feuille de l'ancien moteur en ligne : sa version nouveau moteur viendra de
      // la conversion (sans IA), pas d'une génération en lot
      q.legacyState !== 'published' &&
      (q.shown === 'none' || q.shown === 'review'),
  );
}

/** Feuilles prêtes à publier en lot : validées, à jour, chargeables par le moteur. */
export function questionsReadyToPublish(questions: SheetQuestion[]): SheetQuestion[] {
  return questions.filter(
    (q) => q.linked && q.shown === 'ready' && !q.stale && !!q.row && isEngineReady(q.row),
  );
}

export function ScratchSheetsPanel({
  exerciseId,
  blocks,
}: {
  exerciseId: string;
  blocks: ExerciseBlock[];
}) {
  const sheets = useSheetQuestions(exerciseId, blocks);
  const generate = useGenerateSheet(exerciseId);
  const setStatus = useSetSheetStatus(exerciseId);
  const generating = useIsGenerating(exerciseId);
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const stopRef = useRef(false);
  // panneau démonté (changement de page) : on arrête la boucle après la question en cours
  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );

  const linked = sheets.questions.filter((q) => q.linked);
  const counts = linked.reduce(
    (acc, q) => {
      acc[q.shown] = (acc[q.shown] ?? 0) + 1;
      return acc;
    },
    {} as Partial<Record<SheetState, number>>,
  );
  const todo = questionsToGenerate(sheets.questions);
  const ready = questionsReadyToPublish(sheets.questions);
  const running = progress !== null;

  async function generateAll() {
    stopRef.current = false;
    setMessage(null);
    const list = todo;
    let done = 0;
    const failures: string[] = [];
    for (const q of list) {
      if (stopRef.current) break;
      setProgress({ done, total: list.length, label: `Question ${q.index + 1}` });
      try {
        await generate.mutateAsync({
          question: q,
          onAttempt: (a) =>
            setProgress({
              done,
              total: list.length,
              label: a > 1 ? `Question ${q.index + 1} · relance ${a}/3` : `Question ${q.index + 1}`,
            }),
        });
      } catch (e) {
        failures.push(`Question ${q.index + 1} : ${generationErrorMessage(e)}`);
        // fonction absente, budget atteint, droits refusés… : inutile de continuer
        if (isFatalGenerationError(e)) break;
      }
      done += 1;
    }
    setProgress(null);
    setMessage(
      failures.length
        ? failures.slice(0, 3).join(' · ')
        : stopRef.current
          ? `Arrêté après ${done} question${done > 1 ? 's' : ''}.`
          : `${done} question${done > 1 ? 's' : ''} traitée${done > 1 ? 's' : ''} : teste les feuilles prêtes, puis publie-les.`,
    );
  }

  async function publishReady() {
    if (!ready.length) return;
    if (
      !window.confirm(
        `Publier ${ready.length} feuille${ready.length > 1 ? 's' : ''} ? Les élèves les verront dans le player.`,
      )
    )
      return;
    setMessage(null);
    try {
      await setStatus.mutateAsync({ ids: ready.map((q) => q.row!.id), status: 'published' });
      setMessage(
        `${ready.length} feuille${ready.length > 1 ? 's' : ''} publiée${ready.length > 1 ? 's' : ''}.`,
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section className="mb-6" data-testid="scratch-sheets-panel">
      <header className="mb-3 flex items-center gap-2">
        <PenLine className="text-primary-500 h-4 w-4" aria-hidden />
        <h2 className="font-display text-ink-900 text-[15px] font-extrabold">
          Feuilles de brouillon
        </h2>
      </header>

      <div className="border-ink-200 rounded-2xl border bg-white p-4">
        {sheets.isLoading ? (
          <p className="text-ink-500 font-sans text-sm">Chargement…</p>
        ) : sheets.error ? (
          <p className="font-sans text-sm text-red-600">{sheets.error.message}</p>
        ) : !sheets.anyLinked ? (
          <p className="text-ink-500 font-sans text-sm">
            Pour l’instant, la feuille de brouillon ne s’applique qu’aux questions reliées à une
            SKILLCARD. Relie une question à une carte pour lui donner une feuille.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-ink-600 font-sans text-sm">
              {linked.length} question{linked.length > 1 ? 's' : ''} reliée
              {linked.length > 1 ? 's' : ''} à une SKILLCARD. L’IA propose la feuille, le moteur la
              vérifie, tu la testes et tu la publies : aucune IA ne tourne quand l’élève joue.
            </p>
            <ul className="flex flex-wrap gap-2" aria-label="État des feuilles">
              {ORDER.filter((s) => counts[s]).map((s) => (
                <li key={s} className="inline-flex items-center gap-1.5">
                  <SheetStatePill state={s} />
                  <span className="text-ink-700 font-sans text-xs font-semibold">
                    {counts[s]}
                    <span className="sr-only"> {SHEET_STATE_LABEL[s]}</span>
                  </span>
                </li>
              ))}
            </ul>

            {running && (
              <div className="space-y-1.5">
                <div
                  role="status"
                  className="text-ink-700 flex items-center gap-2 font-sans text-sm"
                >
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {progress.label} — {progress.done} / {progress.total}
                </div>
                <div
                  role="progressbar"
                  aria-label="Génération des feuilles"
                  aria-valuemin={0}
                  aria-valuemax={progress.total}
                  aria-valuenow={progress.done}
                  className="bg-ink-100 h-1.5 overflow-hidden rounded-full"
                >
                  <div
                    className="bg-primary-500 h-full rounded-full transition-all"
                    style={{
                      width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {running ? (
                <button
                  type="button"
                  onClick={() => {
                    stopRef.current = true;
                  }}
                  className={cn(BTN, 'border-ink-200 text-ink-700 bg-white')}
                >
                  <Square className="h-4 w-4" aria-hidden /> Arrêter après cette question
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void generateAll()}
                  disabled={!todo.length || generating || setStatus.isPending}
                  className={cn(
                    BTN,
                    'ocean:border-violet-400/40 ocean:text-violet-300 border-violet-200 bg-violet-50 text-violet-800',
                  )}
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  {todo.length
                    ? `Générer les feuilles manquantes (${todo.length})`
                    : 'Aucune feuille à générer'}
                </button>
              )}
              {ready.length > 0 && (
                <button
                  type="button"
                  onClick={() => void publishReady()}
                  disabled={running || generating || setStatus.isPending}
                  className={cn(
                    BTN,
                    'ocean:text-[#052e1f] border-emerald-300 bg-emerald-600 text-white',
                  )}
                >
                  Publier les feuilles prêtes ({ready.length})
                </button>
              )}
              <Link
                to="/tutor/exercises/$exerciseId/test"
                params={{ exerciseId }}
                className={cn(
                  BTN,
                  'ocean:border-sky-400/40 ocean:text-sky-300 border-sky-200 bg-white text-sky-800',
                )}
              >
                <Play className="h-4 w-4" aria-hidden /> Tester dans le player
              </Link>
            </div>
            {message && (
              <p role="status" className="text-ink-600 font-sans text-xs">
                {message}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
